import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Mail, Loader2, User, GitMerge, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * SalesmanRecipientsModal — CRUD for salesman-wise Outstanding email
 * subscriptions (Feb 2026 · merge/aliases added July 2026).
 *
 * Each salesman appearing in the LATEST snapshot can be mapped to one
 * or more email addresses. Every time a fresh Excel is uploaded the
 * server emails a personalised digest to each salesman with mapped
 * addresses — containing ONLY their customers.
 *
 * MERGE (July 2026 user request):
 *   When the same real salesman shows up in the ERP under different
 *   spellings (e.g. "HASSAN MASOO", "H MASOO", "HASSAN M"), the admin
 *   can now check multiple rows, click "Merge selected", pick which
 *   name becomes the primary, and the backend collapses them into a
 *   single subscription with the OTHERS stored as aliases. Rows in the
 *   next Excel upload for ANY of those names then route to the same
 *   email digest, producing ONE combined email covering everyone.
 *   Aliases render as small chips under the primary name with an ✕ to
 *   split the subscription back apart.
 */
const SalesmanRecipientsModal = ({ onClose, authHeaders }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);          // salesman being saved
  const [merging, setMerging] = useState(false);       // background merge in flight
  const [recipients, setRecipients] = useState([]);    // [{salesman, emails, aliases, updated_at, updated_by}]
  const [known, setKnown] = useState([]);              // ["HASSAN MASOO", ...]
  const [drafts, setDrafts] = useState({});            // {salesman -> "a@b.com,c@d.com"} (comma-separated draft in the input)
  const [search, setSearch] = useState('');            // Feb 2026 — real-time filter by salesman name / email
  const [selected, setSelected] = useState({});        // {salesman -> true} — multi-select for merge
  const [mergeTargetOpen, setMergeTargetOpen] = useState(false); // toggles the primary-picker popover
  const [mergePrimary, setMergePrimary] = useState(''); // radio choice inside the popover
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/outstanding/salesman-recipients`, { headers: authHeaders });
      setRecipients(r.data.recipients || []);
      setKnown(r.data.known_salesmen || []);
      // Seed drafts from server truth so first render doesn't show stale strings.
      const d = {};
      (r.data.recipients || []).forEach((row) => { d[row.salesman] = (row.emails || []).join(', '); });
      setDrafts(d);
      // Any alias name that got merged into a primary is no longer a
      // standalone row — clear it out of `selected` if present.
      setSelected({});
    } catch (e) {
      toast({ title: 'Could not load recipients', description: e.response?.data?.detail || 'Try again.' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Names that are aliased under some primary must NOT appear as a
  // standalone row — they're already visually part of the primary
  // (rendered as chips below its name). Compute the union once.
  const aliasedNames = useMemo(() => {
    const s = new Set();
    recipients.forEach((r) => (r.aliases || []).forEach((a) => s.add(a)));
    return s;
  }, [recipients]);

  // Merge known salesmen + already-configured recipients so the list
  // shows every salesman we know about, even the ones with 0 emails.
  const rows = useMemo(() => {
    const byKey = new Map();
    recipients.forEach((r) => byKey.set(r.salesman, {
      ...r,
      aliases: r.aliases || [],
      hasEmails: (r.emails || []).length > 0,
      inSnapshot: known.includes(r.salesman),
    }));
    known.forEach((k) => {
      if (aliasedNames.has(k)) return; // rendered as a chip under its primary — skip
      if (!byKey.has(k)) byKey.set(k, {
        salesman: k, emails: [], aliases: [], hasEmails: false, inSnapshot: true,
      });
    });
    return Array.from(byKey.values()).sort((a, b) => {
      // In-snapshot first, then by name.
      if (a.inSnapshot !== b.inSnapshot) return a.inSnapshot ? -1 : 1;
      return a.salesman.localeCompare(b.salesman);
    });
  }, [recipients, known, aliasedNames]);

  // Feb 2026 — real-time filter as admin types in the search bar.
  // Matches on salesman name OR any of their registered emails OR any
  // alias name, case-insensitive substring.
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      if ((row.salesman || '').toLowerCase().includes(q)) return true;
      const draft = (drafts[row.salesman] || '').toLowerCase();
      if (draft.includes(q)) return true;
      if ((row.aliases || []).some((a) => (a || '').toLowerCase().includes(q))) return true;
      return (row.emails || []).some((e) => (e || '').toLowerCase().includes(q));
    });
  }, [rows, drafts, search]);

  const parseEmails = (draft) => (draft || '')
    .split(/[,;\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0);

  const putSalesman = (salesman, emails, aliases) => axios.put(
    `${API}/admin/outstanding/salesman-recipients/${encodeURIComponent(salesman)}`,
    { emails, aliases },
    { headers: authHeaders },
  );

  const save = async (salesman) => {
    const emails = parseEmails(drafts[salesman]);
    const row = rows.find((x) => x.salesman === salesman);
    const aliases = row?.aliases || [];
    setSaving(salesman);
    try {
      const r = await putSalesman(salesman, emails, aliases);
      const cleaned = r.data.emails || [];
      setDrafts((p) => ({ ...p, [salesman]: cleaned.join(', ') }));
      toast({
        title: r.data.deleted ? 'Cleared' : 'Saved',
        description: r.data.deleted ? `${salesman} will no longer receive digests.` : `${salesman} · ${cleaned.length} email(s)`,
      });
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSaving(null); }
  };

  const clear = async (salesman) => {
    if (!window.confirm(`Remove all emails for "${salesman}"? They will stop receiving digests immediately.`)) return;
    setSaving(salesman);
    try {
      await axios.delete(
        `${API}/admin/outstanding/salesman-recipients/${encodeURIComponent(salesman)}`,
        { headers: authHeaders },
      );
      toast({ title: 'Removed', description: `${salesman} will no longer receive digests.` });
      await load();
    } catch (e) {
      if (e.response?.status === 404) {
        setDrafts((p) => ({ ...p, [salesman]: '' }));
        toast({ title: 'Cleared', description: 'No emails were registered.' });
      } else {
        toast({ title: 'Remove failed', description: e.response?.data?.detail || 'Try again.' });
      }
    } finally { setSaving(null); }
  };

  // ---------------- MERGE (July 2026) ----------------
  const selectedNames = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selectedCount = selectedNames.length;

  const toggleSelect = (name) => setSelected((p) => {
    const next = { ...p };
    if (next[name]) delete next[name]; else next[name] = true;
    return next;
  });

  const openMergePicker = () => {
    if (selectedCount < 2) return;
    // Default primary = the selected row that already has the most
    // emails (usually the one the admin has already configured).
    const best = selectedNames
      .map((n) => rows.find((r) => r.salesman === n))
      .filter(Boolean)
      .sort((a, b) => (b.emails?.length || 0) - (a.emails?.length || 0))[0];
    setMergePrimary(best?.salesman || selectedNames[0]);
    setMergeTargetOpen(true);
  };

  const runMerge = async () => {
    if (!mergePrimary || selectedCount < 2) return;
    // Union of every selected row's current emails so nothing is lost
    // when the backend collapses them into one entry.
    const seen = new Set();
    const emailsUnion = [];
    selectedNames.forEach((n) => {
      const row = rows.find((r) => r.salesman === n);
      (row?.emails || []).forEach((e) => {
        const ec = String(e || '').toLowerCase();
        if (ec && !seen.has(ec)) { seen.add(ec); emailsUnion.push(ec); }
      });
    });
    // Aliases = every other selected name + any aliases those rows
    // already carried (backend also folds nested aliases in, but sending
    // them explicitly makes the intent obvious in the audit trail).
    const aliasSet = new Set();
    selectedNames.forEach((n) => {
      if (n !== mergePrimary) aliasSet.add(n);
      const row = rows.find((r) => r.salesman === n);
      (row?.aliases || []).forEach((a) => { if (a !== mergePrimary) aliasSet.add(a); });
    });
    setMerging(true);
    try {
      const r = await putSalesman(mergePrimary, emailsUnion, Array.from(aliasSet));
      toast({
        title: 'Merged',
        description: `${selectedCount} entries → 1 subscription (${mergePrimary}). ${(r.data.aliases || []).length} alias(es).`,
      });
      setMergeTargetOpen(false);
      setMergePrimary('');
      await load();
    } catch (e) {
      toast({ title: 'Merge failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setMerging(false); }
  };

  const removeAlias = async (primary, alias) => {
    if (!window.confirm(`Split "${alias}" back out of "${primary}"? It will re-appear as its own row with no emails.`)) return;
    const row = rows.find((r) => r.salesman === primary);
    const remaining = (row?.aliases || []).filter((a) => a !== alias);
    const emails = parseEmails(drafts[primary]);
    setSaving(primary);
    try {
      await putSalesman(primary, emails, remaining);
      toast({ title: 'Alias removed', description: `${alias} is now its own subscription.` });
      await load();
    } catch (e) {
      toast({ title: 'Update failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSaving(null); }
  };
  // ---------------- /MERGE ----------------

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="outstanding-salesman-recipients-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
          <div>
            <h3 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#0078D4]" /> Salesman email subscriptions
            </h3>
            <p className="text-xs text-neutral-500">
              Each salesman with a registered email address receives a personalised digest containing only their own customers whenever a fresh Excel is uploaded. Independent of category-wise subscriptions.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full" data-testid="outstanding-salesman-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Merge action bar — appears when 2+ rows are checked (July 2026). */}
        {selectedCount >= 2 && (
          <div
            className="sticky top-[92px] bg-[#0078D4]/95 backdrop-blur text-white px-6 py-3 flex items-center justify-between gap-3 z-10"
            data-testid="outstanding-salesman-merge-bar"
          >
            <div className="text-sm">
              <strong>{selectedCount} salesmen selected.</strong> Merge into one subscription so a single combined email is sent covering all of them.
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSelected({})}
                className="rounded-full"
                data-testid="outstanding-salesman-merge-clear-btn"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={openMergePicker}
                className="bg-white text-[#0078D4] hover:bg-white/90 rounded-full"
                data-testid="outstanding-salesman-merge-open-btn"
              >
                <GitMerge className="w-4 h-4 mr-1" /> Merge {selectedCount}
              </Button>
            </div>
          </div>
        )}

        {/* Primary-picker popover — inline card that anchors below the merge bar. */}
        {mergeTargetOpen && (
          <div className="mx-6 mt-4 rounded-2xl border border-[#0078D4]/30 bg-[#DEECF9]/40 p-4" data-testid="outstanding-salesman-merge-picker">
            <div className="text-sm font-semibold text-[#0F172A] mb-2">Which name should be the primary?</div>
            <p className="text-[11px] text-neutral-600 mb-3">
              Emails and future rows will be filed under the primary. The others become <em>aliases</em> — the ERP can still emit them and rows still route to the same combined email.
            </p>
            <div className="space-y-1 mb-3">
              {selectedNames.map((n) => (
                <label key={n} className="flex items-center gap-2 text-sm text-[#0F172A] cursor-pointer" data-testid={`outstanding-salesman-merge-primary-${n}`}>
                  <input
                    type="radio"
                    name="merge-primary"
                    value={n}
                    checked={mergePrimary === n}
                    onChange={() => setMergePrimary(n)}
                  />
                  <span className="font-medium">{n}</span>
                  <span className="text-[10px] text-neutral-500">
                    {(rows.find((r) => r.salesman === n)?.emails?.length || 0)} email(s)
                  </span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setMergeTargetOpen(false); setMergePrimary(''); }}
                className="rounded-full"
                data-testid="outstanding-salesman-merge-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={runMerge}
                disabled={!mergePrimary || merging}
                className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full"
                data-testid="outstanding-salesman-merge-confirm-btn"
              >
                {merging ? <Loader2 className="w-4 h-4 animate-spin" /> : <><GitMerge className="w-4 h-4 mr-1" /> Confirm merge</>}
              </Button>
            </div>
          </div>
        )}

        <div className="p-6 space-y-3" data-testid="outstanding-salesman-recipients-list">
          {!loading && rows.length > 0 && (
            <div className="relative">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by salesman name or email…"
                className="text-sm pr-16"
                data-testid="outstanding-salesman-search-input"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-neutral-500 hover:text-[#0078D4] px-2 py-0.5"
                  data-testid="outstanding-salesman-search-clear"
                >
                  Clear
                </button>
              )}
              <p className="text-[11px] text-neutral-500 mt-1">
                Showing {visibleRows.length} of {rows.length}
                {selectedCount > 0 && <> · {selectedCount} selected</>}
              </p>
            </div>
          )}

          {loading && (
            <div className="text-center py-8 text-neutral-500">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading salesmen…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-center py-8 text-neutral-500 text-sm">
              No salesmen found. Upload an Outstanding snapshot first — the app will surface every distinct salesman here.
            </div>
          )}
          {!loading && rows.length > 0 && visibleRows.length === 0 && (
            <div className="text-center py-6 text-neutral-400 text-sm" data-testid="outstanding-salesman-search-empty">
              No salesmen match &quot;{search}&quot;.
            </div>
          )}
          {!loading && visibleRows.map((row) => (
            <div
              key={row.salesman}
              className={`rounded-2xl border p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 ${selected[row.salesman] ? 'border-[#0078D4] bg-[#DEECF9]/40' : row.hasEmails ? 'border-[#0078D4]/40 bg-[#DEECF9]/20' : 'border-neutral-200 bg-white'}`}
              data-testid={`outstanding-salesman-row-${row.salesman}`}
            >
              {/* Multi-select checkbox — July 2026 merge feature. */}
              <label className="flex-shrink-0 self-start sm:self-center pt-1 sm:pt-0 cursor-pointer" title="Select for merging">
                <input
                  type="checkbox"
                  checked={!!selected[row.salesman]}
                  onChange={() => toggleSelect(row.salesman)}
                  className="w-4 h-4 accent-[#0078D4] cursor-pointer"
                  data-testid={`outstanding-salesman-select-${row.salesman}`}
                />
              </label>
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center">
                <User className="w-5 h-5 text-neutral-500" />
              </div>
              <div className="flex-1 min-w-0 w-full">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <div className="font-semibold text-sm text-[#0F172A] truncate">{row.salesman}</div>
                  {!row.inSnapshot && (
                    <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">Not in latest snapshot</span>
                  )}
                  {row.hasEmails && (
                    <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">Active</span>
                  )}
                </div>
                {/* Alias chips — July 2026. Removing a chip splits the alias back out. */}
                {(row.aliases || []).length > 0 && (
                  <div
                    className="flex items-center gap-1 flex-wrap mb-2"
                    data-testid={`outstanding-salesman-aliases-${row.salesman}`}
                  >
                    <span className="text-[10px] uppercase tracking-wider text-[#0078D4] font-semibold mr-1">Aliases:</span>
                    {row.aliases.map((a) => (
                      <span
                        key={a}
                        className="inline-flex items-center gap-1 text-[11px] bg-[#0078D4]/10 text-[#0078D4] border border-[#0078D4]/30 px-2 py-0.5 rounded-full"
                        data-testid={`outstanding-salesman-alias-${row.salesman}-${a}`}
                      >
                        {a}
                        <button
                          type="button"
                          onClick={() => removeAlias(row.salesman, a)}
                          className="hover:text-red-600"
                          title={`Split "${a}" back out`}
                          data-testid={`outstanding-salesman-alias-remove-${row.salesman}-${a}`}
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <Input
                  value={drafts[row.salesman] ?? ''}
                  onChange={(e) => setDrafts((p) => ({ ...p, [row.salesman]: e.target.value }))}
                  placeholder="one@mrpco.net, two@mrpco.net"
                  className="text-sm"
                  data-testid={`outstanding-salesman-emails-input-${row.salesman}`}
                />
                <p className="text-[11px] text-neutral-500 mt-1">Separate multiple addresses with commas.</p>
              </div>
              <div className="flex gap-2 flex-shrink-0 self-stretch sm:self-center">
                <Button
                  size="sm"
                  onClick={() => save(row.salesman)}
                  disabled={saving === row.salesman}
                  className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full"
                  data-testid={`outstanding-salesman-save-btn-${row.salesman}`}
                >
                  {saving === row.salesman ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Save</>}
                </Button>
                {row.hasEmails && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => clear(row.salesman)}
                    disabled={saving === row.salesman}
                    className="rounded-full text-red-600 hover:bg-red-50 border-red-200"
                    data-testid={`outstanding-salesman-clear-btn-${row.salesman}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SalesmanRecipientsModal;
